
import React, { useState, useMemo } from 'react';
import { User, UserRole, MaintenanceTicket, TicketHistoryEntry } from '../types';
import { MOCK_TICKETS } from '../mockData';
import { Plus, Search, Send, X, Info } from 'lucide-react';

interface TicketsProps {
  user: User;
  onStatusChange: (message: string) => void;
}

const Tickets: React.FC<TicketsProps> = ({ user, onStatusChange }) => {
  const [tickets, setTickets] = useState<MaintenanceTicket[]>(MOCK_TICKETS);
  const [isCreating, setIsCreating] = useState(false);
  const [newIssue, setNewIssue] = useState('');
  const [newPriority, setNewPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');
  const [selectedTicket, setSelectedTicket] = useState<MaintenanceTicket | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const matchesStatus = statusFilter === 'ALL' || t.status === statusFilter;
      const matchesPriority = priorityFilter === 'ALL' || t.priority === priorityFilter;
      const matchesSearch = t.issue.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            t.ownerName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesPriority && matchesSearch;
    });
  }, [tickets, statusFilter, priorityFilter, searchQuery]);

  const handleOpenTicket = (t: MaintenanceTicket) => {
    setSelectedTicket(t);
  };

  const handleCreateTicket = () => {
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + 7);

    const ticket: MaintenanceTicket = {
      id: `T-${Date.now()}`,
      ownerId: user.id,
      ownerName: user.name,
      apartment: user.apartment || 'N/A',
      issue: newIssue,
      status: 'PENDING',
      priority: newPriority,
      createdAt: today.toISOString(),
      dueDate: dueDate.toISOString().split('T')[0],
      history: [{
        id: `h-${Date.now()}`,
        timestamp: today.toISOString(),
        status: 'PENDING',
        changedBy: user.name,
        comment: 'Барањето е поднесено.'
      }]
    };
    setTickets([ticket, ...tickets]);
    setNewIssue('');
    setIsCreating(false);
  };

  const updateStatus = (ticketId: string, newStatus: MaintenanceTicket['status']) => {
    setTickets(prev => prev.map(t => {
      if (t.id === ticketId) {
        const historyEntry: TicketHistoryEntry = {
          id: `h-${Date.now()}`,
          timestamp: new Date().toISOString(),
          status: newStatus,
          changedBy: user.name,
          comment: `Статусот е променет во ${newStatus}`
        };
        const updated = { ...t, status: newStatus, history: [historyEntry, ...t.history] };
        
        if (user.role !== UserRole.TENANT) {
          onStatusChange(`Барањето за "${t.issue}" е променето во статус: ${getStatusLabel(newStatus)}`);
        }

        if (selectedTicket?.id === ticketId) setSelectedTicket(updated);
        return updated;
      }
      return t;
    }));
  };

  const getPriorityLabel = (p: string) => {
    switch(p) { case 'HIGH': return 'Висок'; case 'MEDIUM': return 'Среден'; case 'LOW': return 'Низок'; default: return p; }
  };

  const getStatusLabel = (s: string) => {
    switch(s) { case 'PENDING': return 'Во исчекување'; case 'IN_PROGRESS': return 'Во тек'; case 'RESOLVED': return 'Решено'; default: return s; }
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Одржување</h1>
          <p className="text-slate-500">Следете и пријавувајте дефекти.</p>
        </div>
        {(user.role === UserRole.TENANT) && (
          <button 
            onClick={() => setIsCreating(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-indigo-700 flex items-center shadow-lg shadow-indigo-200"
          >
            <Plus className="w-5 h-5 mr-2" /> Пријави дефект
          </button>
        )}
      </div>

      {isCreating && (
        <div className="bg-white p-6 rounded-2xl border border-indigo-100 shadow-xl space-y-4 animate-in slide-in-from-top duration-300">
          <h3 className="font-bold text-slate-900">Ново барање за поправка</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <textarea 
                value={newIssue}
                onChange={(e) => setNewIssue(e.target.value)}
                placeholder="Опишете го проблемот..."
                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Приоритет</label>
              <select 
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value as any)}
                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl"
              >
                <option value="LOW">Низок</option>
                <option value="MEDIUM">Среден</option>
                <option value="HIGH">Висок</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-slate-500 font-semibold">Откажи</button>
            <button onClick={handleCreateTicket} className="px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold flex items-center">
              Поднеси <Send className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:border-indigo-100 transition-all">
        <div className="p-4 bg-slate-50 border-b flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Пребарај..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" 
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border rounded-lg py-2 px-3 focus:outline-none">
            <option value="ALL">Сите статуси</option>
            <option value="PENDING">Во исчекување</option>
            <option value="IN_PROGRESS">Во тек</option>
            <option value="RESOLVED">Решено</option>
          </select>
        </div>

        <table className="w-full">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold border-b">
            <tr>
              <th className="px-6 py-4 text-left">ИД</th>
              <th className="px-6 py-4 text-left">Опис</th>
              <th className="px-6 py-4 text-left">Приоритет</th>
              <th className="px-6 py-4 text-left">Статус</th>
              <th className="px-6 py-4 text-right">Детали</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredTickets.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50 cursor-pointer group" onClick={() => handleOpenTicket(t)}>
                <td className="px-6 py-4 text-xs">#{t.id.slice(-4)}</td>
                <td className="px-6 py-4 text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{t.issue}</td>
                <td className="px-6 py-4"><span className="text-xs uppercase font-bold text-slate-600">{getPriorityLabel(t.priority)}</span></td>
                <td className="px-6 py-4"><span className="text-xs font-semibold">{getStatusLabel(t.status)}</span></td>
                <td className="px-6 py-4 text-right"><button className="text-indigo-600 font-bold text-sm hover:underline">Види</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setSelectedTicket(null)} />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col p-8 animate-in slide-in-from-right duration-300">
             <div className="flex justify-between items-center mb-8 pb-4 border-b">
               <div>
                 <h2 className="text-2xl font-bold text-slate-900">Детали за барањето</h2>
                 <p className="text-xs text-slate-400 font-bold tracking-widest uppercase">Референца: {selectedTicket.id}</p>
               </div>
               <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-slate-100 rounded-full"><X /></button>
             </div>
             
             <div className="flex-1 overflow-y-auto">
               <p className="font-bold text-xs text-slate-400 mb-2 uppercase tracking-widest">ОПИС</p>
               <p className="text-lg text-slate-800 font-medium mb-8 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100">{selectedTicket.issue}</p>
               
               <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-4 rounded-2xl bg-indigo-50 border border-indigo-100">
                    <span className="block text-[10px] font-bold text-indigo-400 uppercase mb-1">Пријавено од</span>
                    <span className="block text-sm font-bold text-indigo-900">{selectedTicket.ownerName}</span>
                    <span className="text-xs text-indigo-700">Стан {selectedTicket.apartment}</span>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Краен рок</span>
                    <span className="block text-sm font-bold text-slate-900">{selectedTicket.dueDate}</span>
                    <span className="text-xs text-slate-500">За 7 дена</span>
                  </div>
               </div>

               <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-200">
                    <span className="text-xs font-bold text-slate-400 uppercase">Промени статус:</span>
                    {(user.role === UserRole.PRESIDENT || user.role === UserRole.DEPUTY || user.role === UserRole.SUPER_ADMIN) ? (
                      <select 
                        className="border-none bg-indigo-600 text-white rounded-lg text-xs font-bold p-2 focus:ring-0 cursor-pointer shadow-lg shadow-indigo-100"
                        value={selectedTicket.status}
                        onChange={(e) => updateStatus(selectedTicket.id, e.target.value as any)}
                      >
                        <option value="PENDING">Во исчекување</option>
                        <option value="IN_PROGRESS">Во тек</option>
                        <option value="RESOLVED">Решено</option>
                      </select>
                    ) : (
                      <span className="text-sm font-bold text-indigo-600">{getStatusLabel(selectedTicket.status)}</span>
                    )}
                  </div>
               </div>

               <div className="mt-8">
                 <h4 className="font-bold text-slate-900 mb-6 flex items-center text-sm uppercase tracking-widest"><Info className="mr-2 w-4 h-4 text-indigo-500" /> Историја на промени</h4>
                 <div className="relative border-l-2 border-slate-100 ml-2 pl-6 space-y-8">
                   {selectedTicket.history.map((h, i) => (
                     <div key={h.id} className="relative">
                       <div className={`absolute -left-[31px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm ${i === 0 ? 'bg-indigo-600 scale-125' : 'bg-slate-300'}`} />
                       <p className="text-sm font-bold text-slate-800">{h.comment}</p>
                       <p className="text-xs text-slate-400 font-medium">{h.changedBy} • {new Date(h.timestamp).toLocaleDateString('mk-MK', { hour: '2-digit', minute: '2-digit' })}</p>
                     </div>
                   ))}
                 </div>
               </div>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
